import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  TextField,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Loader from "@components/Loader/Loader";
import Pictures from "@components/Pictures/Pictures";
import HomeFormButtons from "@components/Home/HomeFormButtons/HomeFormButtons";
import { PhotoSearchFilters, useGetPhotosQuery } from "@store/photoApi";
import { Photo } from "@models/photo";
import { UseFormReturn } from "react-hook-form";
import { useDebounceValue } from "@hooks/useDebounceValue";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useTranslation } from "react-i18next";

const ImageContainer: FC<{
  onSave: (photos: Photo[]) => void;
  excludePhotos: Photo[];
  form: UseFormReturn<PhotoSearchFilters>;
}> = ({ onSave, excludePhotos, form: { reset, watch, register } }) => {
  const { t } = useTranslation();
  const [checkedPhotos, setCheckedPhotos] = useState<Set<Photo>>(new Set());
  const debouncedFilters = useDebounceValue(watch(), 500);
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: photos, isLoading } = useGetPhotosQuery(
    {
      patientId: selectedPatientId!,
      ...debouncedFilters,
    },
    {
      skip: !selectedPatientId,
    },
  );

  const filteredPhotos = useMemo(() => {
    if (!photos?.data?.length) {
      return [];
    }
    const excludeIds = new Set(excludePhotos.map(({ id }) => id));

    return photos.data.filter((photo) => !excludeIds.has(photo.id));
  }, [photos, excludePhotos]);

  useEffect(() => {
    setCheckedPhotos(new Set());
  }, [filteredPhotos]);

  const allChecked = useMemo(() => {
    return (
      !!filteredPhotos?.length && checkedPhotos.size === filteredPhotos.length
    );
  }, [filteredPhotos, checkedPhotos]);

  const checkPhoto = useCallback((photo: Photo) => {
    setCheckedPhotos((photos: Set<Photo>) => {
      const newSet = new Set(photos);
      if (newSet.has(photo)) {
        newSet.delete(photo);
      } else {
        newSet.add(photo);
      }
      return newSet;
    });
  }, []);

  const toggleCheckAll = useCallback(() => {
    if (!filteredPhotos.length) {
      return;
    }
    setCheckedPhotos(new Set(allChecked ? [] : Array.from(filteredPhotos)));
  }, [filteredPhotos, allChecked]);

  const save = useCallback(() => {
    onSave(Array.from(checkedPhotos));
    setCheckedPhotos(new Set());
    reset({ search: "" });
  }, [onSave, checkedPhotos, reset]);

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <TextField
        {...register("search")}
        type="text"
        size="small"
        variant="outlined"
        fullWidth
        placeholder={t("albums.addPhotosDialog.searchInput.placeholder")}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon />
              </InputAdornment>
            ),
          },
        }}
      />

      <Box marginTop="16px">
        <FormControlLabel
          control={
            <Checkbox
              checked={allChecked}
              onChange={toggleCheckAll}
              disabled={!filteredPhotos.length}
            />
          }
          label={t("albums.addPhotosDialog.selectAllCheckboxLabel")}
        />
      </Box>

      <Box marginTop="20px" overflow="auto">
        {isLoading ? (
          <Loader />
        ) : (
          <Pictures
            pictures={filteredPhotos}
            checkedPictures={checkedPhotos}
            onCheck={checkPhoto}
            onSelect={checkPhoto}
          />
        )}
      </Box>

      <Box marginTop="auto">
        <HomeFormButtons>
          <Button
            disabled={!checkedPhotos.size}
            variant="contained"
            size="large"
            onClick={save}
          >
            {t("albums.addPhotosDialog.saveButton")}
          </Button>
        </HomeFormButtons>
      </Box>
    </Box>
  );
};

export default ImageContainer;
